<template>
  <div class="payment-settings">
    <!-- Header -->
    <div class="ps-header mb-4">
      <h4 class="fw-semi-bold mb-2">
        <i class="fa fa-credit-card mr-2"></i>
        Payment Collection Settings
      </h4>
      <p class="text-muted mb-0">
        Configure how your practice collects patient payments via SMS. 
        Select a payment provider and customize your payment link settings.
      </p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2 text-muted">Loading payment settings...</p>
    </div>

    <!-- Main Content -->
    <div v-if="!loading" class="payment-cards">
      
      <!-- Enable/Disable Card -->
      <b-card class="payment-card" :class="{ 'is-active': config.enabled }">
        <template #header>
          <div class="card-header-content">
            <div class="header-left">
              <i class="fa fa-power-off"></i>
              <span class="header-title">Enable Payment Collection</span>
            </div>
            <div class="header-right">
              <b-form-checkbox
                v-model="config.enabled"
                switch
                size="lg"
                :disabled="!canToggleEnable"
                @change="onEnabledChange"
                class="enable-toggle"
              >
                <span :class="config.enabled ? 'text-success' : 'text-muted'">
                  {{ config.enabled ? 'Enabled' : 'Disabled' }}
                </span>
              </b-form-checkbox>
            </div>
          </div>
        </template>
        
        <div class="card-body-content">
          <p class="description-text">
            When enabled, staff can send payment links to patients via SMS from the Aging/Patient Balances page.
          </p>

          <!-- Setup-readiness checklist. Payment collection can't be enabled
               until every required item is configured (or OD posting is
               deliberately turned off). This is the at-a-glance state that
               prevents the "charging but not posting to Open Dental" gap. -->
          <div v-if="config.provider" class="setup-readiness mt-2">
            <div v-if="readinessReady" class="text-success">
              <i class="fa fa-check-circle mr-1"></i>
              Setup complete — payment collection is ready to enable.
            </div>
            <div v-else>
              <div class="fw-semi-bold text-warning mb-1">
                <i class="fa fa-exclamation-triangle mr-1"></i>
                Finish setup before enabling payment collection:
              </div>
              <ul class="mb-0 pl-3">
                <li v-for="item in readinessMissing" :key="item.key" class="text-muted">
                  {{ item.label }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </b-card>

      <!-- Payment Provider Selection Card -->
      <b-card class="payment-card" :class="{ 'is-active': config.enabled && config.provider }">
        <template #header>
          <div class="card-header-content">
            <div class="header-left">
              <i class="fa fa-building"></i>
              <span class="header-title">Payment Provider</span>
            </div>
            <div class="header-right">
              <span v-if="config.provider" class="badge badge-success">
                {{ getProviderName(config.provider) }}
              </span>
              <span v-else class="badge badge-secondary">
                Not Selected
              </span>
            </div>
          </div>
        </template>
        
        <div class="card-body-content">
          <p class="description-text">
            Choose the payment processor for handling patient payments. 
            Each provider requires a merchant account with that service.
          </p>

          <!-- Provider Selection Grid -->
          <div class="section-block">
            <h6 class="section-title">
              <i class="fa fa-list"></i>
              Select Provider
            </h6>
            
            <div class="providers-grid">
              <!-- CardPointe -->
              <div 
                class="provider-option" 
                :class="{ 
                  'is-selected': config.provider === 'cardpointe',
                  'is-disabled': false
                }"
                @click="selectProvider('cardpointe')"
              >
                <div class="provider-checkbox">
                  <i :class="config.provider === 'cardpointe' ? 'fa fa-dot-circle-o' : 'fa fa-circle-o'"></i>
                </div>
                <div class="provider-content">
                  <div class="provider-header-row">
                    <div class="provider-name">
                      <i class="fa fa-building text-primary"></i>
                      <strong>CardPointe</strong>
                      <small class="text-muted">by Fiserv</small>
                    </div>
                    <span class="badge badge-success">Available</span>
                  </div>
                  <ul class="provider-features">
                    <li><i class="fa fa-check text-success"></i> Direct to your bank account</li>
                    <li><i class="fa fa-check text-success"></i> Credit/Debit cards</li>
                    <li><i class="fa fa-check text-success"></i> ACH/Bank transfers (lower fees)</li>
                    <li><i class="fa fa-check text-success"></i> Popular in dental industry</li>
                  </ul>
                </div>
              </div>

              <!-- Stripe (Stripe Connect — practice links their own stripe.com account) -->
              <div
                class="provider-option"
                :class="{
                  'is-selected': config.provider === 'stripe',
                  'is-disabled': false
                }"
                @click="selectProvider('stripe')"
              >
                <div class="provider-checkbox">
                  <i :class="config.provider === 'stripe' ? 'fa fa-dot-circle-o' : 'fa fa-circle-o'"></i>
                </div>
                <div class="provider-content">
                  <div class="provider-header-row">
                    <div class="provider-name">
                      <i class="fa fa-cc-stripe text-info"></i>
                      <strong>Stripe</strong>
                      <small class="text-muted">stripe.com</small>
                    </div>
                    <span class="badge badge-success">Available</span>
                  </div>
                  <ul class="provider-features">
                    <li><i class="fa fa-check text-success"></i> Connect your existing Stripe account</li>
                    <li><i class="fa fa-check text-success"></i> Funds go directly to your Stripe balance</li>
                    <li><i class="fa fa-check text-success"></i> Credit/Debit cards</li>
                    <li><i class="fa fa-check text-success"></i> No account? Sign up inline at Stripe</li>
                  </ul>
                </div>
              </div>

              <!-- Moolah (SA-gated) -->
              <div
                v-if="isSuperAdmin"
                class="provider-option"
                :class="{
                  'is-selected': config.provider === 'moolah',
                  'is-disabled': false
                }"
                @click="selectProvider('moolah')"
              >
                <div class="provider-checkbox">
                  <i :class="config.provider === 'moolah' ? 'fa fa-dot-circle-o' : 'fa fa-circle-o'"></i>
                </div>
                <div class="provider-content">
                  <div class="provider-header-row">
                    <div class="provider-name">
                      <i class="fa fa-money text-success"></i>
                      <strong>Moolah</strong>
                      <span class="badge badge-warning" style="font-size: 0.7em; vertical-align: middle;" title="Only visible to Super Admins">SA</span>
                      <small class="text-muted">moolah.com (Poynt)</small>
                    </div>
                    <span class="badge badge-success">Available</span>
                  </div>
                  <ul class="provider-features">
                    <li><i class="fa fa-check text-success"></i> In-office terminals</li>
                    <li><i class="fa fa-check text-success"></i> Credit/Debit cards</li>
                    <li><i class="fa fa-check text-success"></i> Integrated POS</li>
                    <li><i class="fa fa-check text-success"></i> Contactless payments</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-card>

      <!-- CardPointe Configuration Card (only shown when CardPointe is selected) -->
      <b-card 
        v-if="config.provider === 'cardpointe'" 
        class="payment-card"
        :class="{ 'is-active': config.enabled && cardpointeConfigValid }"
      >
        <template #header>
          <div class="card-header-content">
            <div class="header-left">
              <i class="fa fa-cog"></i>
              <span class="header-title">CardPointe Configuration</span>
            </div>
            <div class="header-right">
              <span v-if="connectionTestResult?.success" class="badge badge-success">
                <i class="fa fa-check mr-1"></i> Connected
              </span>
              <span v-else-if="cardpointeConfigValid" class="badge badge-warning">
                Not Verified
              </span>
              <span v-else class="badge badge-secondary">
                Incomplete
              </span>
            </div>
          </div>
        </template>
        
        <div class="card-body-content">
          <p class="description-text">
            Enter your CardPointe/Fiserv merchant account credentials. 
            You can find these in your CardPointe portal.
          </p>

          <b-alert variant="info" show class="mb-3">
            <i class="fa fa-info-circle mr-2"></i>
            <strong>Don't have a CardPointe account?</strong> 
            Contact your payment processor or 
            <a href="https://www.cardpointe.com" target="_blank">visit CardPointe</a> 
            to set up a merchant account.
          </b-alert>

          <!-- Credentials Section -->
          <div class="section-block">
            <h6 class="section-title">
              <i class="fa fa-key"></i>
              API Credentials
            </h6>
            
            <b-row>
              <b-col md="6" class="mb-4">
                <label class="form-label fw-semi-bold">Merchant ID (MID)</label>
                <b-form-input
                  v-model="config.cardpointe.mid"
                  placeholder="e.g., 496160873888"
                  :disabled="false"
                />
                <p class="text-muted small mt-2 mb-0">Found in your CardPointe portal</p>
              </b-col>
              <b-col md="6" class="mb-4">
                <label class="form-label fw-semi-bold">HPP Site ID</label>
                <b-form-input
                  v-model="config.cardpointe.hppSiteId"
                  placeholder="e.g., your-site-name"
                  :disabled="false"
                />
                <p class="text-muted small mt-2 mb-0">Your hosted payment page subdomain</p>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6" class="mb-4">
                <label class="form-label fw-semi-bold">API Username</label>
                <b-form-input
                  v-model="config.cardpointe.username"
                  placeholder="API username"
                  :disabled="false"
                />
              </b-col>
              <b-col md="6" class="mb-4">
                <label class="form-label fw-semi-bold">API Password</label>
                <b-form-input
                  v-model="config.cardpointe.password"
                  type="password"
                  :placeholder="config.cardpointe.passwordSet ? '••••••••••••' : 'Enter API password'"
                  :disabled="false"
                />
                <p v-if="config.cardpointe.passwordSet" class="text-success small mt-2 mb-0">
                  <i class="fa fa-check-circle mr-1"></i>Password saved (enter new to change)
                </p>
              </b-col>
            </b-row>

            <div class="d-flex align-items-center mt-2">
              <b-button 
                variant="outline-primary" 
                size="sm"
                @click="testConnection"
                :disabled="!canTestConnection || testingConnection"
              >
                <span v-if="testingConnection">
                  <b-spinner small class="mr-1" />
                  Testing...
                </span>
                <span v-else>
                  <i class="fa fa-plug mr-1"></i>
                  Test Connection
                </span>
              </b-button>
              
              <div v-if="connectionTestResult" class="ml-3">
                <span v-if="connectionTestResult.success" class="text-success">
                  <i class="fa fa-check-circle mr-1"></i>
                  {{ connectionTestResult.message }}
                </span>
                <span v-else class="text-danger">
                  <i class="fa fa-times-circle mr-1"></i>
                  {{ connectionTestResult.message }}
                </span>
              </div>
            </div>
          </div>

          <!-- Payment Methods Section -->
          <div class="section-block">
            <h6 class="section-title">
              <i class="fa fa-credit-card"></i>
              Payment Methods
            </h6>
            
            <div class="payment-methods-grid">
              <div 
                class="payment-method-option"
                :class="{ 'is-selected': config.cardpointe.acceptCreditCard }"
                @click="togglePaymentMethod('creditCard')"
              >
                <div class="method-checkbox">
                  <i :class="config.cardpointe.acceptCreditCard ? 'fa fa-check-square' : 'fa fa-square-o'"></i>
                </div>
                <div class="method-content">
                  <span class="method-label"><i class="fa fa-credit-card mr-1"></i> Credit/Debit Cards</span>
                  <small class="method-description">Visa, Mastercard, Amex, Discover</small>
                </div>
              </div>

              <div 
                class="payment-method-option"
                :class="{ 'is-selected': config.cardpointe.acceptACH }"
                @click="togglePaymentMethod('ach')"
              >
                <div class="method-checkbox">
                  <i :class="config.cardpointe.acceptACH ? 'fa fa-check-square' : 'fa fa-square-o'"></i>
                </div>
                <div class="method-content">
                  <span class="method-label"><i class="fa fa-bank mr-1"></i> ACH/Bank Transfer</span>
                  <small class="method-description">Lower fees than card payments</small>
                </div>
                <span class="badge badge-success ml-auto">Lower Fees</span>
              </div>
            </div>
          </div>
        </div>
      </b-card>

      <!-- Stripe Connect Configuration Card (only shown when Stripe is selected) -->
      <b-card
        v-if="config.provider === 'stripe'"
        class="payment-card"
        :class="{ 'is-active': config.enabled && stripeStatus.connected && stripeStatus.chargesEnabled }"
      >
        <template #header>
          <div class="card-header-content">
            <div class="header-left">
              <i class="fa fa-cc-stripe"></i>
              <span class="header-title">Stripe Account</span>
            </div>
            <div class="header-right">
              <span v-if="stripeStatus.connected && stripeStatus.chargesEnabled" class="badge badge-success">
                <i class="fa fa-check mr-1"></i> Connected &amp; Verified
              </span>
              <span v-else-if="stripeStatus.connected" class="badge badge-warning">
                <i class="fa fa-clock-o mr-1"></i> Pending Verification
              </span>
              <span v-else class="badge badge-secondary">
                Not Connected
              </span>
            </div>
          </div>
        </template>

        <div class="card-body-content">
          <!-- NOT CONNECTED — show explainer + Connect button -->
          <template v-if="!stripeStatus.connected">
            <p class="description-text">
              Connect your existing Stripe account to accept patient payments through Teamio.
              Funds settle directly into your Stripe balance — Teamio never holds your money.
              Stripe charges their standard processing fees on your account; Teamio takes nothing extra.
            </p>

            <b-alert variant="info" show class="mb-3">
              <i class="fa fa-info-circle mr-2"></i>
              <strong>Don't have a Stripe account yet?</strong>
              No problem — you can sign up for one inline during the connection flow.
              Stripe handles all account verification, banking, and identity checks.
            </b-alert>

            <div class="actions-section">
              <b-button
                variant="primary"
                size="lg"
                :disabled="connectingStripe"
                @click="connectStripe"
              >
                <b-spinner v-if="connectingStripe" small class="mr-1" />
                <i v-else class="fa fa-cc-stripe mr-2"></i>
                Connect with Stripe
              </b-button>
            </div>
          </template>

          <!-- CONNECTED — show account info + verification banner + actions -->
          <template v-else>
            <p class="description-text">
              Your Stripe account is connected. Patient payments collected through Teamio will
              route directly to this account.
            </p>

            <!-- Verification banner if Stripe still needs more info -->
            <b-alert
              v-if="!stripeStatus.chargesEnabled || (stripeStatus.requirementsCurrentlyDue && stripeStatus.requirementsCurrentlyDue.length > 0)"
              variant="warning"
              show
              class="mb-3"
            >
              <i class="fa fa-clock-o mr-2"></i>
              <strong>Stripe is still verifying your account.</strong>
              Until verification completes, charges may be held or rejected.
              <span v-if="stripeStatus.requirementsCurrentlyDue && stripeStatus.requirementsCurrentlyDue.length > 0">
                Stripe needs the following from you:
                <ul class="mb-0 mt-1">
                  <li v-for="req in stripeStatus.requirementsCurrentlyDue" :key="req">{{ req }}</li>
                </ul>
              </span>
              <div class="mt-2">
                <a href="https://dashboard.stripe.com/account/onboarding" target="_blank" rel="noopener">
                  <i class="fa fa-external-link mr-1"></i>
                  Complete account setup at Stripe
                </a>
              </div>
            </b-alert>

            <b-alert
              v-else-if="stripeStatus.stale"
              variant="warning"
              show
              class="mb-3"
            >
              <i class="fa fa-exclamation-triangle mr-2"></i>
              We couldn't reach Stripe just now to refresh your account status.
              Showing the last known values.
            </b-alert>

            <div class="section-block">
              <h6 class="section-title">
                <i class="fa fa-info-circle"></i>
                Connection Details
              </h6>

              <b-row>
                <b-col md="6" class="mb-3">
                  <label class="form-label fw-semi-bold">Stripe Account</label>
                  <div class="form-control-plaintext">
                    <code>{{ stripeStatus.accountId }}</code>
                  </div>
                </b-col>
                <b-col md="6" class="mb-3">
                  <label class="form-label fw-semi-bold">Connected On</label>
                  <div class="form-control-plaintext">
                    {{ formatStripeDate(stripeStatus.connectedAt) || '—' }}
                  </div>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="6" class="mb-3">
                  <label class="form-label fw-semi-bold">Charges Enabled</label>
                  <div class="form-control-plaintext">
                    <span v-if="stripeStatus.chargesEnabled" class="text-success">
                      <i class="fa fa-check-circle mr-1"></i>Yes
                    </span>
                    <span v-else class="text-warning">
                      <i class="fa fa-clock-o mr-1"></i>Pending
                    </span>
                  </div>
                </b-col>
                <b-col md="6" class="mb-3">
                  <label class="form-label fw-semi-bold">Payouts Enabled</label>
                  <div class="form-control-plaintext">
                    <span v-if="stripeStatus.payoutsEnabled" class="text-success">
                      <i class="fa fa-check-circle mr-1"></i>Yes
                    </span>
                    <span v-else class="text-warning">
                      <i class="fa fa-clock-o mr-1"></i>Pending
                    </span>
                  </div>
                </b-col>
              </b-row>
            </div>

            <div class="actions-section d-flex">
              <b-button
                variant="outline-primary"
                size="sm"
                :disabled="loadingStripeStatus"
                @click="refreshStripeStatus"
              >
                <b-spinner v-if="loadingStripeStatus" small class="mr-1" />
                <i v-else class="fa fa-refresh mr-1"></i>
                Refresh Status
              </b-button>

              <b-button
                variant="outline-danger"
                size="sm"
                class="ml-2"
                :disabled="disconnectingStripe"
                @click="disconnectStripe"
              >
                <b-spinner v-if="disconnectingStripe" small class="mr-1" />
                <i v-else class="fa fa-unlink mr-1"></i>
                Disconnect Stripe
              </b-button>
            </div>
          </template>
        </div>
      </b-card>

      <!-- Moolah Configuration Card (only shown when Moolah is selected, SA-gated) -->
      <b-card
        v-if="config.provider === 'moolah' && isSuperAdmin"
        class="payment-card"
        :class="{ 'is-active': config.enabled && moolahConfig.businessID && moolahConfig.apiKeySet }"
      >
        <template #header>
          <div class="card-header-content">
            <div class="header-left">
              <i class="fa fa-cog"></i>
              <span class="header-title">Moolah Configuration</span>
              <span class="badge badge-warning" style="font-size: 0.7em; vertical-align: middle;" title="Only visible to Super Admins">SA</span>
            </div>
            <!-- Reflects a REAL probe (get-moolah-devices), run on mount and on
                 every save. Before 2026-09-16 this read the session-only result
                 of the Test Connection button, which nothing hydrated — so a
                 correctly-wired, actively-charging site said "Not Verified"
                 forever. See moolahStatus(). -->
            <div class="header-right">
              <span v-if="moolahProbe.state === 'checking'" class="badge badge-secondary">
                <b-spinner small class="mr-1" /> Checking…
              </span>
              <span v-else-if="moolahProbe.state === 'connected'" class="badge badge-success">
                <i class="fa fa-check mr-1"></i>
                Connected<span v-if="moolahProbe.deviceCount"> · {{ moolahProbe.deviceCount }} terminal{{ moolahProbe.deviceCount === 1 ? '' : 's' }}</span>
              </span>
              <span v-else-if="moolahProbe.state === 'rejected'" class="badge badge-danger">
                <i class="fa fa-times mr-1"></i> Key rejected
              </span>
              <span v-else-if="moolahProbe.state === 'unreachable'" class="badge badge-warning">
                <i class="fa fa-exclamation-triangle mr-1"></i> Couldn't reach Moolah
              </span>
              <span v-else class="badge badge-secondary">
                Incomplete
              </span>
            </div>
          </div>
        </template>

        <div class="card-body-content">
          <p class="description-text">
            Enter your Moolah merchant account credentials.
            The Business ID is in your Moolah/Poynt portal. The API Key is not —
            Moolah support issues it separately for each practice.
          </p>

          <!-- Credentials Section -->
          <div class="section-block">
            <h6 class="section-title">
              <i class="fa fa-key"></i>
              API Credentials
            </h6>

            <b-row>
              <b-col md="6" class="mb-4">
                <label class="form-label fw-semi-bold">Business ID</label>
                <b-form-input
                  v-model="moolahConfig.businessID"
                  placeholder="e.g., abc123-def456-..."
                  :disabled="false"
                />
                <p class="text-muted small mt-2 mb-0">Found in your Moolah/Poynt portal</p>
              </b-col>
              <b-col md="6" class="mb-4">
                <label class="form-label fw-semi-bold">API Key</label>
                <b-form-input
                  v-model="moolahConfig.apiKey"
                  type="password"
                  :placeholder="moolahConfig.apiKeySet ? '••••••••••••' : 'Enter API key'"
                  :disabled="false"
                />
                <p v-if="moolahConfig.apiKeySet" class="text-success small mt-2 mb-0">
                  <i class="fa fa-check-circle mr-1"></i>API Key saved (enter new to change)
                </p>
                <p v-else class="text-muted small mt-2 mb-0">
                  Request this from Moolah support — it isn't shown in the Poynt portal.
                  A string like <code>&hellip;=urn:aid:&hellip;</code> is your Business ID and
                  Application ID, not the API Key.
                </p>
              </b-col>
            </b-row>

            <div class="d-flex align-items-center mt-2">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="testMoolahConnection"
                :disabled="!moolahConfig.businessID || !moolahConfig.apiKeySet || moolahProbe.state === 'checking'"
              >
                <span v-if="moolahProbe.state === 'checking'">
                  <b-spinner small class="mr-1" />
                  Testing...
                </span>
                <span v-else>
                  <i class="fa fa-plug mr-1"></i>
                  Test Connection
                </span>
              </b-button>

              <div v-if="moolahProbe.message" class="ml-3">
                <span v-if="moolahProbe.state === 'connected'" class="text-success">
                  <i class="fa fa-check-circle mr-1"></i>
                  {{ moolahProbe.message }}
                </span>
                <span v-else-if="moolahProbe.state === 'checking'" class="text-muted">
                  {{ moolahProbe.message }}
                </span>
                <span v-else class="text-danger">
                  <i class="fa fa-times-circle mr-1"></i>
                  {{ moolahProbe.message }}
                </span>
              </div>
            </div>
            <p v-if="moolahConfig.businessID && !moolahConfig.apiKeySet" class="text-muted small mt-2 mb-0">
              Save the API key first — the connection is tested against the stored key.
            </p>
          </div>

          <!-- Actions -->
          <div class="actions-section">
            <b-button
              variant="success"
              size="sm"
              :disabled="savingMoolahConfig"
              @click="saveMoolahConfig"
            >
              <b-spinner v-if="savingMoolahConfig" small class="mr-1" />
              <i v-else class="fa fa-save mr-1"></i>
              Save Moolah Settings
            </b-button>
          </div>
        </div>
      </b-card>

      <!-- Open Dental Posting (Moolah) ───────────────────────────────────────
           Surfaces the previously-invisible TEXT_TO_PAY_OD_WRITE_ENABLED flag
           as a deliberate, visible decision. Default ON (write-back recommended,
           so Teamio + Open Dental stay in sync). Turning it OFF is an explicit
           opt-out (manual reconciliation) — never a silent default. Born from
           the Tulip Tree gap where cards charged but nothing posted to OD. -->
      <b-card
        v-if="config.provider === 'moolah'"
        class="payment-card"
        :class="{ 'is-active': odWriteOn }"
      >
        <template #header>
          <div class="card-header-content">
            <div class="header-left">
              <i class="fa fa-exchange"></i>
              <span class="header-title">Open Dental Posting</span>
            </div>
            <div class="header-right">
              <b-form-checkbox
                :checked="odWriteOn"
                switch
                size="lg"
                @change="onOdWriteToggle"
              >
                <span :class="odWriteOn ? 'text-success' : 'text-muted'">
                  {{ odWriteOn ? 'On' : 'Off' }}
                </span>
              </b-form-checkbox>
            </div>
          </div>
        </template>

        <div class="card-body-content">
          <p class="description-text">
            When on, every successful card payment is automatically recorded in
            Open Dental (payment + paysplit on the patient ledger) so Teamio and
            Open Dental stay in sync. This requires a <strong>Default</strong>
            Card → Open Dental PayType below.
          </p>
          <b-alert :show="!odWriteOn" variant="warning" class="mb-0">
            <i class="fa fa-exclamation-triangle mr-1"></i>
            Off — payments will be charged but <strong>not</strong> recorded in
            Open Dental. Your team will need to post them manually.
          </b-alert>
        </div>
      </b-card>

      <!-- Card Brand → OD PayType Mapping (Moolah only) ──────────────────────
           Tells Teamio which Open Dental PayType (definition Category 10) to
           stamp on a posted payment, per card brand. Visible to regular admins.
           Saved as part of the main Save flow (folded into saveConfig). -->
      <b-card
        v-if="config.provider === 'moolah'"
        class="payment-card"
      >
        <template #header>
          <div class="card-header-content">
            <div class="header-left">
              <i class="fa fa-credit-card"></i>
              <span class="header-title">Card Brand → Open Dental PayType</span>
            </div>
          </div>
        </template>

        <div class="card-body-content">
          <p class="description-text">
            When a payment posts back to Open Dental, Teamio records it under the
            Payment Type you choose here — matched to the card brand used. Set a
            <strong>Default</strong> so brands without a specific mapping still post.
          </p>

          <div v-if="loadingOdDefs" class="text-center py-3">
            <b-spinner small /> <span class="text-muted">Loading payment types…</span>
          </div>

          <template v-else>
            <div class="section-block">
              <b-row
                v-for="row in brandRows"
                :key="row.key"
                class="align-items-center mb-3"
              >
                <b-col sm="4">
                  <label class="form-label fw-semi-bold mb-0">{{ row.label }}</label>
                </b-col>
                <b-col sm="8">
                  <select
                    class="form-control"
                    v-model="textToPay.creditCardPayTypeByBrand[row.key]"
                  >
                    <option :value="null">— Not set —</option>
                    <option
                      v-for="pt in odPayTypes"
                      :key="pt.DefNum"
                      :value="pt.DefNum"
                    >{{ pt.ItemName }}</option>
                  </select>
                </b-col>
              </b-row>
            </div>

            <!-- Soft, non-blocking hint: a per-brand mapping exists but no Default. -->
            <b-alert :show="showDefaultHint" variant="warning" class="mb-0">
              Set a <strong>Default</strong> PayType so card brands without a specific
              mapping still post to Open Dental.
            </b-alert>

            <p v-if="odPayTypes.length === 0" class="text-muted mb-0">
              No Open Dental payment types found for this office.
            </p>
          </template>
        </div>
      </b-card>

      <!-- Manual Methods → OD PayType (Moolah only) ──────────────────────
           Tells Teamio which Open Dental PayType to stamp when the chairside
           Tx Presenter deposit panel marks a payment as Cash or Check. The
           money doesn't move through Moolah on these paths (already physically
           taken at the chair); we just write it to OD so end-of-day reports
           land in the right bucket — same fix Naveen needs for card-brand
           categorization, extended to cash + check. -->
      <b-card
        v-if="config.provider === 'moolah'"
        class="payment-card"
      >
        <template #header>
          <div class="card-header-content">
            <div class="header-left">
              <i class="fa fa-money"></i>
              <span class="header-title">Manual Methods → Open Dental PayType</span>
            </div>
          </div>
        </template>

        <div class="card-body-content">
          <p class="description-text">
            When a TC marks a Tx Presenter deposit as Cash or Check chairside,
            Open Dental records the payment under the Payment Type you pick here.
            Leave blank to disable that method in the chairside deposit panel.
          </p>

          <div v-if="loadingOdDefs" class="text-center py-3">
            <b-spinner small /> <span class="text-muted">Loading payment types…</span>
          </div>

          <template v-else>
            <div class="section-block">
              <b-row
                v-for="row in manualRows"
                :key="row.key"
                class="align-items-center mb-3"
              >
                <b-col sm="4">
                  <label class="form-label fw-semi-bold mb-0">{{ row.label }}</label>
                </b-col>
                <b-col sm="8">
                  <select
                    class="form-control"
                    v-model="textToPay.manualMethodPayType[row.key]"
                  >
                    <option :value="null">— Not set (disable {{ row.label }} chairside) —</option>
                    <option
                      v-for="pt in odPayTypes"
                      :key="pt.DefNum"
                      :value="pt.DefNum"
                    >{{ pt.ItemName }}</option>
                  </select>
                </b-col>
              </b-row>
            </div>

            <p v-if="odPayTypes.length === 0" class="text-muted mb-0">
              No Open Dental payment types found for this office.
            </p>
          </template>
        </div>
      </b-card>

      <!-- Treatment Plan Prepayment (Tx Presenter only) ─────────────────────
           The OD unearned type (definition Category 29) used when a patient
           prepays a signed treatment plan. Saved with the main Save flow. -->
      <b-card
        v-if="config.provider === 'moolah' && txPresenterEnabled"
        class="payment-card"
      >
        <template #header>
          <div class="card-header-content">
            <div class="header-left">
              <i class="fa fa-money"></i>
              <span class="header-title">Treatment Plan Prepayment</span>
            </div>
          </div>
        </template>

        <div class="card-body-content">
          <p class="description-text">
            When a patient prepays for a signed treatment plan, record the payment
            in Open Dental under this Unearned Type.
          </p>

          <div v-if="loadingOdDefs" class="text-center py-3">
            <b-spinner small /> <span class="text-muted">Loading…</span>
          </div>

          <div v-else class="section-block">
            <b-row class="align-items-center mb-0">
              <b-col sm="4">
                <label class="form-label fw-semi-bold mb-0">Prepayment posts as</label>
              </b-col>
              <b-col sm="8">
                <select
                  class="form-control"
                  v-model="textToPay.prepaymentUnearnedType"
                >
                  <option :value="null">— Not set —</option>
                  <option
                    v-for="ut in odUnearnedTypes"
                    :key="ut.DefNum"
                    :value="ut.DefNum"
                  >{{ ut.ItemName }}</option>
                </select>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-card>

      <!-- Payment Link Options Card (only shown when provider is selected) -->
      <b-card
        v-if="config.provider"
        class="payment-card"
      >
        <template #header>
          <div class="card-header-content">
            <div class="header-left">
              <i class="fa fa-link"></i>
              <span class="header-title">Payment Link Options</span>
            </div>
          </div>
        </template>
        
        <div class="card-body-content">
          <p class="description-text">
            Customize how payment links behave when sent to patients.
          </p>

          <!-- Link Settings Section -->
          <div class="section-block">
            <h6 class="section-title">
              <i class="fa fa-sliders"></i>
              Link Settings
            </h6>
            
            <b-row>
              <b-col md="6" class="mb-4">
                <label class="form-label fw-semi-bold">Link Expiration</label>
                <multiselect
                  v-model="selectedExpiry"
                  :options="expiryOptions"
                  :multiple="false"
                  :searchable="false"
                  :show-labels="false"
                  :allow-empty="false"
                  :disabled="false"
                  label="label"
                  track-by="value"
                  placeholder="Select expiration..."
                  @select="onExpiryChange"
                />
                <p class="text-muted small mt-2 mb-0">How long the payment link remains active</p>
              </b-col>
              <b-col md="6" class="mb-4">
                <label class="form-label fw-semi-bold">Default SMS Template</label>
                <multiselect
                  v-model="selectedTemplate"
                  :options="templateOptions"
                  :multiple="false"
                  :searchable="true"
                  :show-labels="false"
                  :allow-empty="true"
                  :disabled="false"
                  label="label"
                  track-by="value"
                  placeholder="Select a template..."
                  @select="onTemplateChange"
                >
                  <template slot="noResult">
                    <span>No matching payment templates.</span>
                  </template>
                  <template slot="noOptions">
                    <span>No payment SMS templates yet. Tag a wiki page as both "payment" and "sms-template" to see it here.</span>
                  </template>
                </multiselect>
                <!-- A default saved before this list was narrowed to payment-tagged
                     pages can no longer be used by the Collect Payment modal, which
                     lists the same set. Say so instead of silently dropping it. -->
                <p v-if="untaggedDefault" class="text-warning small mt-2 mb-0">
                  <i class="fa fa-exclamation-triangle mr-1"></i>
                  Your saved default <strong>{{ untaggedDefault.label }}</strong> isn't tagged
                  <strong>payment</strong>, so it won't be used. Add the tag in Wiki, or pick another template.
                </p>
                <p class="text-muted small mt-2 mb-0">Only shows Wiki pages tagged <strong>payment</strong> and <strong>sms-template</strong>. To make a template appear here, add both tags to it in the Wiki.</p>
              </b-col>
            </b-row>
          </div>

          <!-- Additional Options Section -->
          <div class="section-block">
            <h6 class="section-title">
              <i class="fa fa-cogs"></i>
              Additional Options
            </h6>
            
            <div class="options-grid">
              <div 
                class="option-item"
                :class="{ 'is-selected': config.allowPartialPayments }"
                @click="config.allowPartialPayments = !config.allowPartialPayments"
              >
                <div class="option-checkbox">
                  <i :class="config.allowPartialPayments ? 'fa fa-check-square' : 'fa fa-square-o'"></i>
                </div>
                <div class="option-content">
                  <span class="option-label">Allow partial payments</span>
                  <small class="option-description">Staff can enter a custom amount less than full balance</small>
                </div>
              </div>

              <div 
                class="option-item"
                :class="{ 'is-selected': config.sendEmailReceipt }"
                @click="config.sendEmailReceipt = !config.sendEmailReceipt"
              >
                <div class="option-checkbox">
                  <i :class="config.sendEmailReceipt ? 'fa fa-check-square' : 'fa fa-square-o'"></i>
                </div>
                <div class="option-content">
                  <span class="option-label">Send email receipt</span>
                  <small class="option-description">Email receipt to patient after successful payment</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="actions-section">
            <b-button
              variant="success"
              size="sm"
              :disabled="saving"
              @click="saveConfig"
            >
              <b-spinner v-if="saving" small class="mr-1" />
              <i v-else class="fa fa-save mr-1"></i>
              Save Payment Settings
            </b-button>
          </div>
        </div>
      </b-card>

    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import { extractApiError } from '@/utils/apiError';

export default {
  name: 'PaymentSettings',

  components: {
    Multiselect
  },

  data() {
    return {
      loading: true,
      saving: false,
      testingConnection: false,
      connectionTestResult: null,
      // Set when the site's SAVED defaultTemplateId points at a wiki page that
      // is an SMS template but is NOT tagged 'payment'. Such a default can
      // never be applied (CollectPaymentModal lists the same payment-tagged
      // set), so it is surfaced as a warning rather than silently dropped.
      untaggedDefault: null,
      paymentTemplates: [],
      selectedTemplate: null, // For multiselect

      // Moolah
      moolahConfig: {
        businessID: '',
        apiKey: '',
        apiKeySet: false
      },
      // Result of the real get-moolah-devices probe. state is one of
      // 'incomplete' | 'checking' | 'connected' | 'rejected' | 'unreachable'.
      // Replaced the session-only moolahTestResult (2026-09-16) — nothing ever
      // hydrated that, so a live site read "Not Verified" on every page load.
      moolahProbe: { state: 'incomplete', message: '', deviceCount: 0 },
      savingMoolahConfig: false,

      // Text-to-pay → OD settings (card-brand→PayType map + manual methods + prepayment)
      textToPay: {
        creditCardPayTypeByBrand: { visa: null, mastercard: null, amex: null, discover: null, default: null },
        manualMethodPayType: { cash: null, check: null },
        prepaymentUnearnedType: null,
      },
      odPayTypes: [],       // [{DefNum, ItemName, suggestedBrand}] from OD Cat 10
      odUnearnedTypes: [],  // [{DefNum, ItemName}] from OD Cat 29
      loadingOdDefs: false,
      brandRows: [
        { key: 'visa', label: 'Visa' },
        { key: 'mastercard', label: 'Mastercard' },
        { key: 'amex', label: 'Amex' },
        { key: 'discover', label: 'Discover' },
        { key: 'default', label: 'Default (all other cards)' },
      ],
      // Manual methods (Cash / Check) — the chairside Tx Presenter deposit
      // panel routes "Mark as Cash" / "Mark as Check" through these.
      manualRows: [
        { key: 'cash', label: 'Cash' },
        { key: 'check', label: 'Check' },
      ],

      // Stripe Connect
      stripeStatus: {
        connected: false,
        accountId: null,
        chargesEnabled: false,
        payoutsEnabled: false,
        requirementsCurrentlyDue: [],
        connectedAt: null,
        lastCheckedAt: null,
        stale: false,
      },
      loadingStripeStatus: false,
      connectingStripe: false,
      disconnectingStripe: false,
      
      // Link expiration options
      expiryOptions: [
        { value: 1, label: '1 day' },
        { value: 3, label: '3 days' },
        { value: 7, label: '7 days' },
        { value: 14, label: '14 days' },
        { value: 30, label: '30 days' }
      ],
      selectedExpiry: null, // For multiselect
      
      config: {
        enabled: false,
        provider: '', // 'cardpointe', 'stripe', 'moolah'
        
        // CardPointe specific
        cardpointe: {
          mid: '',
          username: '',
          password: '',
          passwordSet: false, // True if password is already stored on server
          hppSiteId: '',
          acceptCreditCard: true,
          acceptACH: true
        },
        
        // Stripe specific (for future)
        stripe: {
          connected: false,
          accountId: '',
          feeHandling: 'pass_to_patient' // or 'absorb'
        },
        
        // General settings
        linkExpiryDays: 7,
        defaultTemplateId: '',
        allowPartialPayments: true,
        sendEmailReceipt: true,

        // OD-posting decision: null = not yet decided, 'TRUE' = write-back on,
        // 'FALSE' = explicit opt-out. Backs the "Write payments back to Open
        // Dental" toggle and the setup-readiness gate.
        odWriteEnabled: null
      },

      // Server-computed setup readiness: { ready, missing:[{key,label}], details }.
      // Drives the checklist + gates the Enable Payment Collection toggle.
      readiness: null
    };
  },

  computed: {
    ...mapGetters('customer', ['siteID', 'user']),

    isSuperAdmin() {
      return this.user && this.user.Role === 'Super Admin';
    },

    // Keyed map of teamio.settings rows (this component doesn't use the global
    // mixin that exposes the same map). Mirrors ConversationsSettings.
    allSettings() {
      const settings = this.$store.state.customer.settings || [];
      return settings.reduce((acc, s) => { acc[s.Property] = s; return acc; }, {});
    },

    txPresenterEnabled() {
      const s = this.allSettings && this.allSettings.TX_PRESENTER_ENABLED;
      return !!(s && String(s.PropertyValue).toUpperCase() === 'TRUE');
    },

    // Soft, non-blocking hint: a per-brand mapping is set but Default is empty.
    showDefaultHint() {
      const m = this.textToPay.creditCardPayTypeByBrand || {};
      const anyBrandSet = ['visa', 'mastercard', 'amex', 'discover'].some((k) => m[k]);
      return this.config.provider === 'moolah' && anyBrandSet && !m.default;
    },

    canTestConnection() {
      if (this.config.provider === 'cardpointe') {
        // Can test if we have mid, username, and either a new password or existing password is set
        return this.config.cardpointe.mid &&
               this.config.cardpointe.username &&
               (this.config.cardpointe.password || this.config.cardpointe.passwordSet);
      }
      return false;
    },

    cardpointeConfigValid() {
      return this.config.cardpointe.mid && 
             this.config.cardpointe.username && 
             (this.config.cardpointe.password || this.config.cardpointe.passwordSet) &&
             this.config.cardpointe.hppSiteId;
    },

    // Template options for multiselect
    templateOptions() {
      return this.paymentTemplates.map(t => ({
        value: t.WikiPageID,
        label: t.Name,
        content: t.Content
      }));
    },

    // ── Setup readiness (mirrors the backend gate in helpers/paymentReadiness.js) ──

    // Whether OD write-back applies to the chosen provider (Moolah only today).
    odWriteSupported() {
      return this.config.provider === 'moolah';
    },

    // Two-way binding for the "Write payments back to Open Dental" toggle.
    // Defaults ON when the office hasn't decided yet (null) — the recommended
    // path — but the decision isn't *recorded* until they Save (which sends
    // 'TRUE'/'FALSE'), so the checklist still asks them to confirm.
    odWriteOn: {
      get() {
        return this.config.odWriteEnabled !== 'FALSE';
      },
      set(on) {
        this.config.odWriteEnabled = on ? 'TRUE' : 'FALSE';
      }
    },

    // Server-truth readiness. null until loadConfig resolves.
    readinessReady() {
      return !!(this.readiness && this.readiness.ready);
    },
    readinessMissing() {
      return (this.readiness && this.readiness.missing) || [];
    },

    // You can ALWAYS turn payments OFF; you can only turn them ON when ready.
    canToggleEnable() {
      return this.config.enabled || this.readinessReady;
    }
  },

  async mounted() {
    // Set default expiry
    this.selectedExpiry = this.expiryOptions.find(o => o.value === 7); // Default 7 days

    await this.loadConfig();
    await this.loadPaymentTemplates();

    // Card-brand → OD PayType mapping + prepayment (Moolah only). loadConfig has
    // set config.provider by now.
    if (this.config.provider === 'moolah') {
      await this.loadTextToPayMappingData();
    }

    // Handle Stripe OAuth callback redirects (?stripe_connected=1 / ?stripe_error=...)
    this.handleStripeOAuthRedirect();
  },

  methods: {
    ...mapActions('alert', ['setLoading2', 'setErrored2', 'setMessage2']),
    ...mapActions('teamio', ['getPaymentConfig', 'savePaymentConfig', 'validateCardpointeCredentials', 'getTextToPaySettings', 'saveTextToPaySettings', 'getOdPaymentDefinitions']),

    getProviderName(provider) {
      const names = {
        cardpointe: 'CardPointe',
        stripe: 'Stripe',
        moolah: 'Moolah'
      };
      return names[provider] || provider;
    },

    async loadConfig() {
      this.loading = true;
      try {
        console.log('Loading payment config for site:', this.siteID);
        const response = await this.getPaymentConfig(this.siteID);
        
        if (response && response.config) {
          const serverConfig = response.config;
          
          // Merge server config with local defaults
          this.config.enabled = serverConfig.enabled || false;
          this.config.provider = serverConfig.provider === 'none' ? '' : (serverConfig.provider || '');

          // OD-posting decision (three-valued) + server-computed setup readiness.
          this.config.odWriteEnabled = serverConfig.odWriteEnabled || null;
          this.readiness = serverConfig.readiness || null;

          // CardPointe
          if (serverConfig.cardpointe) {
            this.config.cardpointe.mid = serverConfig.cardpointe.mid || '';
            this.config.cardpointe.username = serverConfig.cardpointe.username || '';
            this.config.cardpointe.passwordSet = serverConfig.cardpointe.passwordSet || false;
            this.config.cardpointe.password = ''; // Don't populate password from server
            this.config.cardpointe.hppSiteId = serverConfig.cardpointe.hppSiteId || '';
            this.config.cardpointe.acceptCreditCard = serverConfig.cardpointe.acceptCreditCard !== false;
            this.config.cardpointe.acceptACH = serverConfig.cardpointe.acceptACH !== false;
          }
          
          // General settings
          this.config.linkExpiryDays = serverConfig.linkExpiryDays || 7;
          this.config.allowPartialPayments = serverConfig.allowPartialPayments !== false;
          this.config.sendEmailReceipt = serverConfig.sendEmailReceipt !== false;
          this.config.defaultTemplateId = serverConfig.defaultTemplateId || '';
          
          // Set multiselect values
          this.selectedExpiry = this.expiryOptions.find(o => o.value === this.config.linkExpiryDays) || this.expiryOptions[2]; // Default 7 days
          
          // If Moolah is selected, load Moolah config
          if (this.config.provider === 'moolah') {
            this.loadMoolahConfig();
          }

          // If Stripe is selected, fetch fresh Connect status (drives banners)
          if (this.config.provider === 'stripe') {
            this.refreshStripeStatus();
          }

          // If password is set on server and we have valid config, mark connection as potentially valid
          if (this.config.cardpointe.passwordSet && this.cardpointeConfigValid) {
            this.connectionTestResult = {
              success: true,
              message: 'Credentials configured'
            };
          }
        }
      } catch (err) {
        console.error('Error loading payment config:', err);
        // Don't show error toast on initial load if config doesn't exist yet
        if (err.response?.status !== 404) {
          this.$toast.error('Failed to load payment settings');
        }
      } finally {
        this.loading = false;
      }
    },

    async loadTextToPaySettings() {
      try {
        const res = await this.getTextToPaySettings(this.siteID);
        const s = (res && res.settings) || {};
        const map = s.creditCardPayTypeByBrand || {};
        // Normalize into the fixed 5-slot shape so v-model bindings are stable.
        this.textToPay.creditCardPayTypeByBrand = {
          visa: map.visa != null ? map.visa : null,
          mastercard: map.mastercard != null ? map.mastercard : null,
          amex: map.amex != null ? map.amex : null,
          discover: map.discover != null ? map.discover : null,
          default: map.default != null ? map.default : null,
        };
        const manual = s.manualMethodPayType || {};
        this.textToPay.manualMethodPayType = {
          cash: manual.cash != null ? manual.cash : null,
          check: manual.check != null ? manual.check : null,
        };
        this.textToPay.prepaymentUnearnedType = s.prepaymentUnearnedType != null ? s.prepaymentUnearnedType : null;
      } catch (err) {
        console.error('Error loading text-to-pay settings:', err);
        // Non-fatal — leave defaults so the form still renders.
      }
    },

    async loadOdPaymentDefinitions() {
      this.loadingOdDefs = true;
      try {
        const res = await this.getOdPaymentDefinitions(this.siteID);
        this.odPayTypes = (res && res.payTypes) || [];
        this.odUnearnedTypes = (res && res.unearnedTypes) || [];
        this.applyBrandAutoSuggest();
      } catch (err) {
        console.error('Error loading OD payment definitions:', err);
        this.odPayTypes = [];
        this.odUnearnedTypes = [];
      } finally {
        this.loadingOdDefs = false;
      }
    },

    // Pre-select a brand dropdown when (a) it's currently unset AND (b) OD has a
    // PayType whose name matches that brand (backend's suggestedBrand hint).
    // Never overrides a value the office already saved.
    applyBrandAutoSuggest() {
      const map = this.textToPay.creditCardPayTypeByBrand;
      for (const pt of this.odPayTypes) {
        if (pt.suggestedBrand && map[pt.suggestedBrand] == null) {
          map[pt.suggestedBrand] = pt.DefNum;
        }
      }
    },

    // Exact (comma-split) tag match — avoids 'payment' spuriously matching
    // 'prepayment' / 'payment-plan' / 'payment-reminder'. Same helper as
    // FormsUpdateReminderSettings.vue, which warns about 'form' vs 'platform'.
    hasTag(o, tag) {
      if (!o || !o.Tags) return false;
      return String(o.Tags).split(',').map(t => t.trim()).includes(tag);
    },

    async loadPaymentTemplates() {
      try {
        // Fetch the SMS templates server-side (indexed HAVING FIND_IN_SET), then
        // narrow to the PAYMENT-tagged ones here.
        //
        // Both tags are required, and that is not a stylistic choice: the
        // Collect Payment modal — the surface that actually sends the link —
        // lists exactly `payment`-tagged pages (CollectPaymentModal.vue
        // `paymentSmsTemplates`). Offering a wider set here let an office pick a
        // default the send surface could never show. The two lists must agree.
        const result = await this.$store.dispatch('wiki/getWikiPages', {
          siteID: this.siteID,
          tags: 'sms-template'
        });

        // Result is an array where first element contains the wiki pages
        if (result && Array.isArray(result) && result.length > 0 && result[0]) {
          const smsTemplates = result[0].filter(wiki =>
            wiki.IsSmsTemplate === 1 || wiki.IsSmsTemplate === '1'
          );
          this.paymentTemplates = smsTemplates.filter(wiki => this.hasTag(wiki, 'payment'));
          console.log(`Loaded ${this.paymentTemplates.length} payment SMS templates from wiki`);

          // Set selected template if we have a defaultTemplateId
          const savedId = this.config.defaultTemplateId;
          const found = savedId
            ? this.paymentTemplates.find(t => t.WikiPageID === savedId)
            : null;
          if (found) {
            this.selectedTemplate = {
              value: found.WikiPageID,
              label: found.Name,
              content: found.Content
            };
            this.untaggedDefault = null;
          } else if (savedId) {
            // Saved, but not payment-tagged (or gone). Surface it — the value is
            // left on `config` untouched so an unrelated Save doesn't wipe it.
            const orphan = smsTemplates.find(t => t.WikiPageID === savedId);
            this.untaggedDefault = {
              value: savedId,
              label: orphan ? orphan.Name : 'the previously selected template'
            };
          } else {
            this.untaggedDefault = null;
          }
        } else {
          console.warn('No payment SMS templates found in wiki');
          this.paymentTemplates = [];
        }
      } catch (err) {
        console.error('Error loading payment templates:', err);
        this.paymentTemplates = [];
      }
    },

    onExpiryChange(selected) {
      if (selected) {
        this.config.linkExpiryDays = selected.value;
      }
    },

    onTemplateChange(selected) {
      if (selected) {
        this.config.defaultTemplateId = selected.value;
      } else {
        this.config.defaultTemplateId = '';
      }
    },

    selectProvider(provider) {
      // Configure-first model: a provider can be selected and set up at any
      // time. Going live (the "Enable Payment Collection" toggle) is what's
      // gated on the readiness checklist.
      this.config.provider = provider;
      this.connectionTestResult = null;
      this.moolahProbe = { state: 'incomplete', message: '', deviceCount: 0 };
      if (provider === 'moolah') {
        this.loadMoolahConfig();
        this.loadTextToPayMappingData(); // populate brand→PayType dropdowns
      } else if (provider === 'stripe') {
        this.refreshStripeStatus();
      }
    },

    // Loads everything the card-brand mapping + prepayment subsections need.
    // Called from mounted (when provider is already Moolah) and from
    // selectProvider (when the user switches to Moolah this session).
    async loadTextToPayMappingData() {
      try { await this.$store.dispatch('customer/getSettings', { siteID: this.siteID }); } catch (e) { /* non-fatal — drives txPresenterEnabled */ }
      await this.loadTextToPaySettings();
      await this.loadOdPaymentDefinitions();
    },

    onEnabledChange() {
      // Turning payments off no longer wipes the configured provider — the
      // office's setup is preserved so they can re-enable without reconfiguring.
      this.connectionTestResult = null;
    },

    // The "Write payments back to Open Dental" toggle. Turning it OFF is an
    // explicit opt-out (manual reconciliation) — confirm before accepting it,
    // and revert the switch if the office cancels. Turning ON is immediate.
    onOdWriteToggle(on) {
      if (on) {
        this.odWriteOn = true;
        return;
      }
      this.$bvModal.msgBoxConfirm(
        'Payments will still be charged, but they will NOT be recorded in Open Dental — your team will need to post them manually. Turn off Open Dental posting?',
        {
          title: 'Turn off Open Dental posting?',
          okVariant: 'warning',
          okTitle: 'Turn it off',
          cancelTitle: 'Keep it on',
          centered: true,
        }
      ).then((confirmed) => {
        this.odWriteOn = !confirmed; // confirmed → off; cancelled → stay on
      }).catch(() => {
        this.odWriteOn = true; // dialog dismissed → leave on
      });
    },

    togglePaymentMethod(method) {

      if (method === 'creditCard') {
        this.config.cardpointe.acceptCreditCard = !this.config.cardpointe.acceptCreditCard;
      } else if (method === 'ach') {
        this.config.cardpointe.acceptACH = !this.config.cardpointe.acceptACH;
      }
      
      // Ensure at least one method is selected
      if (!this.config.cardpointe.acceptCreditCard && !this.config.cardpointe.acceptACH) {
        this.$toast.warning('At least one payment method must be enabled');
        if (method === 'creditCard') {
          this.config.cardpointe.acceptCreditCard = true;
        } else {
          this.config.cardpointe.acceptACH = true;
        }
      }
    },

    async testConnection() {
      if (!this.canTestConnection) return;

      this.testingConnection = true;
      this.connectionTestResult = null;

      try {
        // If the user typed a new password, send it. Otherwise omit it so
        // the backend tests against the encrypted-at-rest saved password.
        const payload = {
          siteID: this.siteID,
          mid: this.config.cardpointe.mid,
          username: this.config.cardpointe.username,
        };
        if (this.config.cardpointe.password) {
          payload.password = this.config.cardpointe.password;
        }
        const response = await this.validateCardpointeCredentials(payload);
        
        if (response.valid) {
          this.connectionTestResult = {
            success: true,
            message: response.message || 'Connection successful!'
          };
          this.$toast.success('CardPointe connection verified!');
        } else {
          this.connectionTestResult = {
            success: false,
            message: response.message || 'Connection failed'
          };
          this.$toast.error(response.message || 'Connection test failed');
        }
      } catch (err) {
        console.error('Error testing connection:', err);
        this.connectionTestResult = {
          success: false,
          message: err.response?.data?.message || 'Connection failed. Please check your credentials.'
        };
        this.$toast.error('Connection test failed');
      } finally {
        this.testingConnection = false;
      }
    },

    async saveConfig({ silent = false } = {}) {
      // Validation
      if (this.config.enabled && this.config.provider === 'cardpointe') {
        if (!this.config.cardpointe.mid) {
          this.$toast.error('Please enter your Merchant ID');
          return;
        }
        // Only require password if it's not already set
        if (!this.config.cardpointe.username) {
          this.$toast.error('Please enter your API username');
          return;
        }
        if (!this.config.cardpointe.password && !this.config.cardpointe.passwordSet) {
          this.$toast.error('Please enter your API password');
          return;
        }
        if (!this.config.cardpointe.hppSiteId) {
          this.$toast.error('Please enter your HPP Site ID');
          return;
        }
      }

      this.saving = true;

      try {
        // Save the card-brand → OD PayType mapping FIRST (Moolah only), so a
        // validation error blocks the whole save before the main config is
        // persisted (avoids a confusing partial save). The backend rejects bad
        // DefNums with a 400 + specific message.
        if (this.config.provider === 'moolah') {
          await this.saveTextToPaySettings({
            siteID: this.siteID,
            settings: {
              creditCardPayTypeByBrand: this.textToPay.creditCardPayTypeByBrand,
              manualMethodPayType: this.textToPay.manualMethodPayType,
              prepaymentUnearnedType: this.textToPay.prepaymentUnearnedType,
            },
          });
        }

        const payload = {
          siteID: this.siteID,
          enabled: this.config.enabled,
          provider: this.config.provider || 'none',
          cardpointe: {
            mid: this.config.cardpointe.mid,
            username: this.config.cardpointe.username,
            password: this.config.cardpointe.password, // Only sent if user entered a new one
            hppSiteId: this.config.cardpointe.hppSiteId,
            acceptCreditCard: this.config.cardpointe.acceptCreditCard,
            acceptACH: this.config.cardpointe.acceptACH
          },
          linkExpiryDays: this.config.linkExpiryDays,
          allowPartialPayments: this.config.allowPartialPayments,
          sendEmailReceipt: this.config.sendEmailReceipt,
          defaultTemplateId: this.config.defaultTemplateId,
          updatedBy: this.user?.EmployeeID
        };

        // Record the OD-posting decision for providers that support write-back
        // (Moolah today). Defaults to ON ('TRUE') — the toggle is ON unless the
        // office explicitly opted out. Backend persists 'TRUE'/'FALSE' and uses
        // it in the readiness gate.
        if (this.odWriteSupported) {
          payload.odWriteEnabled = this.odWriteOn ? 'TRUE' : 'FALSE';
        }

        await this.savePaymentConfig(payload);

        // Refresh the cached site object so other features (Aging, Collect Payment, etc.)
        // see the new provider without requiring a logout / cache wipe. Sites are
        // hydrated from localStorage on every reload, so we update both layers.
        this.$store.commit('customer/setSitePaymentProvider', {
          siteID: this.siteID,
          provider: payload.provider,
        });

        // Re-pull server-computed readiness so the checklist + Enable gate
        // reflect exactly what was saved (and what the backend will enforce).
        try {
          const fresh = await this.getPaymentConfig(this.siteID);
          if (fresh && fresh.config) {
            this.readiness = fresh.config.readiness || null;
            if (fresh.config.odWriteEnabled) this.config.odWriteEnabled = fresh.config.odWriteEnabled;
          }
        } catch (e) { /* best-effort: a checklist refresh failure must not fail the save */ }

        // Update passwordSet flag since we just saved
        if (this.config.cardpointe.password) {
          this.config.cardpointe.passwordSet = true;
          this.config.cardpointe.password = ''; // Clear the password field
        }

        if (!silent) this.$toast.success('Payment settings saved successfully!');
      } catch (err) {
        console.error('Error saving payment config:', err);
        // Backend returns JSON.stringify(<string>) for errors — read as string
        // when present so slice-6 provider-switch guard message ("Cannot switch
        // provider: N pending Stripe payment(s)…") surfaces to the user.
        const detail = typeof err.response?.data === 'string'
          ? err.response.data
          : err.response?.data?.message;
        // A readiness/guard rejection (409) means the enable didn't take —
        // keep the local toggle in sync with the server (still not enabled).
        if (err.response?.status === 409) {
          this.config.enabled = false;
        }
        this.$toast.error(detail || 'Failed to save payment settings');
      } finally {
        this.saving = false;
      }
    },

    async loadMoolahConfig() {
      try {
        const response = await this.$store.dispatch('moolah/getMoolahConfig', this.siteID);
        if (response && response.config) {
          this.moolahConfig.businessID = response.config.businessID || '';
          this.moolahConfig.apiKeySet = response.config.apiKeySet || false;
        }
      } catch (err) {
        console.error('Error loading Moolah config:', err);
        // Don't show error toast on initial load if config doesn't exist yet
        if (err.response?.status !== 404) {
          this.$toast.error('Failed to load Moolah configuration');
        }
      }
      // The backend stores NO verification state (getMoolahConfig returns only
      // businessID + apiKeySet), so the badge has to earn its answer on every
      // load. Silent: a mount-time probe must not toast at someone who just
      // opened the page.
      this.testMoolahConnection({ silent: true });
    },

    async saveMoolahConfig() {
      if (!this.moolahConfig.businessID) {
        this.$toast.error('Please enter your Business ID');
        return;
      }
      if (!this.moolahConfig.apiKey && !this.moolahConfig.apiKeySet) {
        this.$toast.error('Please enter your API Key');
        return;
      }

      this.savingMoolahConfig = true;

      try {
        await this.$store.dispatch('moolah/saveMoolahConfig', {
          siteID: this.siteID,
          businessID: this.moolahConfig.businessID,
          apiKey: this.moolahConfig.apiKey,
          updatedBy: this.user?.EmployeeID
        });

        // Update apiKeySet flag since we just saved
        if (this.moolahConfig.apiKey) {
          this.moolahConfig.apiKeySet = true;
          this.moolahConfig.apiKey = ''; // Clear the key field
        }

        // Record the provider + refresh the setup-readiness checklist, but do
        // NOT auto-enable. Enabling Payment Collection is now gated on the full
        // checklist (provider + OD-posting decision + Default PayType when
        // write-back is on). Auto-enabling here is exactly what produced the
        // Tulip Tree failure (cards charged, nothing posted to OD) — and the
        // backend readiness gate would reject it now anyway.
        if (this.config.provider !== 'moolah') {
          this.config.provider = 'moolah';
        }
        await this.saveConfig({ silent: true });

        this.$toast.success('Moolah settings saved. Finish the setup checklist, then enable payment collection.');

        // Re-probe against the key that was JUST stored. A save is the one
        // moment the answer can have changed, and "saved" is not "works" —
        // save-moolah-config accepts a key it never authenticates with.
        this.testMoolahConnection({ silent: true });
      } catch (err) {
        console.error('Error saving Moolah config:', err);
        this.$toast.error(err.response?.data?.message || 'Failed to save Moolah settings');
      } finally {
        this.savingMoolahConfig = false;
      }
    },

    /**
     * The REAL Moolah auth test: GET /get-moolah-devices with the site's stored,
     * encrypted key.
     *
     * NOT `validate-moolah-credentials`. That endpoint POSTs a deliberately
     * bogus nonce to /api/token/tokenize and treats ANY non-401/403 as valid —
     * including the 500 Moolah returns for the bad nonce. A wrong key passes it.
     * PAYMENT_SYSTEM.md §11.2 and the moolah-onboarding skill's Common-mistakes
     * table both say so: only /api/devices proves a key. Shipping a green badge
     * from a check the runbook tells people to distrust is worse than no badge.
     *
     * Also proves more than auth: a device list returned THROUGH our API means
     * the stored key decrypts and authenticates from inside the Lambda, which is
     * the thing that has to work at charge time.
     *
     * @param {{silent?: boolean}} opts  silent = mount-time probe, no toasts
     */
    async testMoolahConnection(opts = {}) {
      const silent = opts === true || opts?.silent === true;
      if (!this.moolahConfig.businessID || !this.moolahConfig.apiKeySet) {
        this.moolahProbe = { state: 'incomplete', message: '', deviceCount: 0 };
        return;
      }

      this.moolahProbe = { state: 'checking', message: 'Checking connection…', deviceCount: 0 };

      try {
        const devices = await this.$store.dispatch('moolah/getMoolahDevices', this.siteID);
        const list = Array.isArray(devices) ? devices : (devices?.devices || []);
        const count = list.length;
        this.moolahProbe = {
          state: 'connected',
          deviceCount: count,
          message: count
            ? `Connected — ${count} terminal${count === 1 ? '' : 's'} available`
            : 'Connected — key is valid (no activated terminals; payment links still work)',
        };
        if (!silent) this.$toast.success('Moolah connection verified!');
      } catch (err) {
        console.error('Error testing Moolah connection:', err);
        // Moolah rejects a bad key as 502 "Failed to fetch terminals: Invalid
        // Key" through our wrapper (PAYMENT_SYSTEM.md §10). Distinguish that
        // from "we couldn't get an answer" — one means re-key the site, the
        // other means try again later.
        //
        // extractApiError, NOT err.response.data.message: /opt/common serialises
        // createErrorResponse as a BARE JSON string, so `.message` is always
        // undefined here and the 'Invalid Key' text would never be seen.
        const body = extractApiError(err, '');
        const rejected = /invalid key|unauthor|forbidden/i.test(String(body));
        this.moolahProbe = rejected
          ? { state: 'rejected', deviceCount: 0, message: 'Moolah rejected this API key. Request a new one from Moolah support.' }
          : { state: 'unreachable', deviceCount: 0, message: `Couldn't reach Moolah${body ? ` — ${body}` : ''}. This does not mean the key is wrong.` };
        if (!silent) this.$toast.error(this.moolahProbe.message);
      }
    },

    // ===== Stripe Connect =====================================================

    /**
     * Practice clicked "Connect with Stripe". Backend signs a CSRF state and
     * returns the connect.stripe.com URL. We redirect the current tab there;
     * Stripe redirects back to the OAuth callback, which redirects here with
     * `?stripe_connected=1` (or `?stripe_error=...`).
     */
    async connectStripe() {
      // Configure-first: connecting Stripe is part of setup and doesn't require
      // payments to be enabled yet (enabling is gated on the readiness checklist).
      this.connectingStripe = true;
      try {
        const response = await this.$store.dispatch('teamio/getStripeOAuthUrl', this.siteID);
        if (!response || !response.url) {
          throw new Error('No OAuth URL returned from server');
        }
        // Redirect current tab. Stripe handles auth (or signup), then redirects
        // to our callback Lambda, which redirects back here.
        window.location.href = response.url;
      } catch (err) {
        console.error('Error starting Stripe OAuth:', err);
        this.$toast.error(err.response?.data?.message || 'Failed to start Stripe connection');
        this.connectingStripe = false;
      }
    },

    async refreshStripeStatus() {
      if (!this.siteID) return;
      this.loadingStripeStatus = true;
      try {
        const response = await this.$store.dispatch('teamio/getStripeAccountStatus', this.siteID);
        if (response) {
          this.stripeStatus = {
            connected: !!response.connected,
            accountId: response.accountId || null,
            chargesEnabled: !!response.chargesEnabled,
            payoutsEnabled: !!response.payoutsEnabled,
            requirementsCurrentlyDue: response.requirementsCurrentlyDue || [],
            connectedAt: response.connectedAt || null,
            lastCheckedAt: response.lastCheckedAt || null,
            stale: !!response.stale,
          };
        }
      } catch (err) {
        console.error('Error refreshing Stripe status:', err);
        // Don't toast on background refresh; the verification banner handles UX.
      } finally {
        this.loadingStripeStatus = false;
      }
    },

    async disconnectStripe() {
      if (!this.stripeStatus.connected) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'This will disconnect your Stripe account from Teamio. Patients will no longer be able to pay you through Teamio until you reconnect or pick another provider. Continue?',
        {
          title: 'Disconnect Stripe?',
          okVariant: 'danger',
          okTitle: 'Disconnect',
          cancelTitle: 'Keep Connected',
          centered: true,
        }
      );
      if (!confirmed) return;

      this.disconnectingStripe = true;
      try {
        const response = await this.$store.dispatch('teamio/disconnectStripeAccount', {
          siteID: this.siteID,
          updatedBy: this.user?.EmployeeID,
        });
        this.$toast.success('Stripe account disconnected');
        // Backend may have flipped PaymentProvider to 'none' if we were on stripe.
        if (response && response.paymentProvider) {
          this.config.provider = response.paymentProvider === 'none' ? '' : response.paymentProvider;
        }
        this.stripeStatus = {
          connected: false,
          accountId: null,
          chargesEnabled: false,
          payoutsEnabled: false,
          requirementsCurrentlyDue: [],
          connectedAt: null,
          lastCheckedAt: null,
          stale: false,
        };
      } catch (err) {
        console.error('Error disconnecting Stripe:', err);
        // Backend's common.createErrorResponse returns JSON.stringify(message),
        // so axios delivers it as `data: "<string>"`. Surface that string
        // directly when present (carries the "3 pending payments" detail
        // from slice 6 lifecycle guard); fall back to .message for legacy.
        const detail = typeof err.response?.data === 'string'
          ? err.response.data
          : err.response?.data?.message;
        this.$toast.error(detail || 'Failed to disconnect Stripe');
      } finally {
        this.disconnectingStripe = false;
      }
    },

    formatStripeDate(s) {
      if (!s) return '';
      const d = new Date(s);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString();
    },

    /**
     * Read OAuth callback query params after Stripe redirects us back here:
     *   ?stripe_connected=1&accountId=acct_xxx
     *   ?stripe_error=<code>&stripe_error_description=<text>
     * Show a toast, refresh status, then strip the params from the URL so a
     * page refresh doesn't re-fire the toast.
     */
    handleStripeOAuthRedirect() {
      const q = this.$route?.query || {};
      const hasSuccess = q.stripe_connected === '1' || q.stripe_connected === 1 || q.stripe_connected === true;
      const errorCode = q.stripe_error;
      if (!hasSuccess && !errorCode) return;

      if (hasSuccess) {
        this.$toast.success('Stripe account connected!');
        // Force-select the Stripe provider in the UI so the practice sees the
        // connected state right away (they still have to save to flip
        // PaymentProvider, per the connect-vs-activate split).
        this.config.provider = 'stripe';
        this.refreshStripeStatus();
      } else if (errorCode) {
        const desc = q.stripe_error_description || '';
        const friendly = {
          access_denied: 'You declined to authorize Teamio. No connection was made.',
          invalid_state: 'The connection link expired or was tampered with. Please try again.',
          invalid_callback: 'Stripe redirected back without the expected parameters. Please try again.',
          oauth_token_failed: 'Stripe rejected the connection. Please try again.',
        };
        this.$toast.error(friendly[errorCode] || `Stripe connection failed: ${desc || errorCode}`);
      }

      // Strip the OAuth params so a refresh doesn't replay this. Replace
      // history without leaving a back-button entry.
      const cleanQuery = { ...q };
      delete cleanQuery.stripe_connected;
      delete cleanQuery.stripe_error;
      delete cleanQuery.stripe_error_description;
      delete cleanQuery.siteID;
      delete cleanQuery.accountId;
      this.$router.replace({ query: cleanQuery }).catch(() => { /* duplicate-nav warning */ });
    }
  }
};
</script>

<style lang="scss" scoped>
.payment-settings {
  .ps-header {
    border-bottom: 1px solid #eee;
    padding-bottom: 1rem;
  }
  
  .payment-cards {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
}

// Payment Card (matches report-frequency-card)
.payment-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &.is-active {
    border-color: #28a745;
    box-shadow: 0 0 0 1px rgba(40, 167, 69, 0.2);
  }
  
  // Card Header
  ::v-deep .card-header {
    background: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
    padding: 1rem 1.25rem;
  }
  
  .card-header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    > i {
      font-size: 1.25rem;
      color: #547fff;
      width: 24px;
      text-align: center;
    }
    
    .header-title {
      font-weight: 600;
      font-size: 1.1rem;
      color: #333;
    }
  }
  
  .header-right {
    .enable-toggle {
      margin-bottom: 0;
      
      ::v-deep .custom-control-label {
        padding-left: 0.5rem;
        font-weight: 500;
      }
    }
  }
  
  // Card Body
  .card-body-content {
    padding: 0.5rem;
  }
  
  .description-text {
    font-size: 0.9rem;
    color: #6c757d;
    margin-bottom: 1.5rem;
  }
  
  // Section Blocks
  .section-block {
    background: #f9fafb;
    border-radius: 8px;
    padding: 1rem 1.25rem;
    margin-bottom: 1.25rem;
  }
  
  .section-title {
    font-weight: 600;
    font-size: 0.95rem;
    color: #333;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    i {
      color: #6c757d;
      width: 18px;
    }
  }
  
  .form-label {
    font-size: 0.9rem;
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.5rem;
    display: block;
  }
  
  // Form controls styling
  .custom-select,
  ::v-deep .form-control {
    height: auto;
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
    border-radius: 4px;
    border: 1px solid #ced4da;
    
    &:focus {
      border-color: #80bdff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
    
    &:disabled {
      background-color: #e9ecef;
      opacity: 0.65;
    }
  }
  
  // Multiselect styling
  ::v-deep .multiselect {
    min-height: 38px;
    
    .multiselect__tags {
      min-height: 38px;
      padding: 6px 40px 0 8px;
      border: 1px solid #ced4da;
      border-radius: 4px;
      background: #fff;
      font-size: 0.9rem;
    }
    
    .multiselect__single {
      margin-bottom: 6px;
      font-size: 0.9rem;
      color: #495057;
    }
    
    .multiselect__placeholder {
      color: #6c757d;
      margin-bottom: 6px;
      padding-top: 0;
    }
    
    .multiselect__select {
      height: 36px;
    }
    
    .multiselect__option {
      padding: 10px 12px;
      min-height: 36px;
      font-size: 0.9rem;
      
      &--highlight {
        background: #547fff;
        color: white;
      }
      
      &--selected {
        background: #e3f2fd;
        color: #495057;
        font-weight: 500;
        
        &.multiselect__option--highlight {
          background: #547fff;
          color: white;
        }
      }
    }
    
    .multiselect__content-wrapper {
      border: 1px solid #ced4da;
      border-radius: 0 0 4px 4px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    &.multiselect--disabled {
      opacity: 0.65;
      
      .multiselect__tags {
        background-color: #e9ecef;
      }
      
      .multiselect__select {
        background: transparent;
      }
    }
  }
}

// Provider Selection Grid - Horizontal layout
.providers-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
}

.provider-option {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  min-height: 180px;
  
  &:hover:not(.is-disabled) {
    border-color: #b0b0b0;
    background: #fafafa;
  }
  
  &.is-selected {
    border-color: #28a745;
    background: #f0fff4;
    
    .provider-checkbox i {
      color: #28a745;
    }
  }
  
  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.7;
    background: #f8f9fa;
  }
  
  &.coming-soon {
    .provider-features {
      color: #999;
      
      i {
        color: #999;
      }
    }
  }
  
  .provider-checkbox {
    position: absolute;
    top: 1rem;
    left: 1rem;
    
    i {
      font-size: 1.1rem;
      color: #aaa;
    }
  }
  
  .provider-content {
    flex: 1;
    padding-left: 1.5rem;
  }
  
  .provider-header-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .provider-name {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    i {
      font-size: 1.1rem;
    }
    
    strong {
      font-size: 0.95rem;
    }
    
    small {
      font-size: 0.8rem;
    }
  }
  
  .provider-features {
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: 0.8rem;
    
    li {
      padding: 0.15rem 0;
      
      i {
        width: 14px;
        margin-right: 0.35rem;
        font-size: 0.75rem;
      }
    }
    
    &.muted {
      color: #999;
      
      li i {
        color: #999;
      }
    }
  }
}

.coming-soon-badge {
  position: absolute;
  top: -8px;
  right: 16px;
  background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
  color: white;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
}

// Payment Methods Grid
.payment-methods-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.payment-method-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    border-color: #b0b0b0;
    background: #fafafa;
  }
  
  &.is-selected {
    border-color: #28a745;
    background: #f0fff4;
    
    .method-checkbox i {
      color: #28a745;
    }
  }
  
  .method-checkbox {
    flex-shrink: 0;
    
    i {
      font-size: 1.1rem;
      color: #aaa;
    }
  }
  
  .method-content {
    flex: 1;
    min-width: 0;
  }
  
  .method-label {
    display: block;
    font-weight: 500;
    font-size: 0.9rem;
    color: #333;
    margin-bottom: 0.1rem;
  }
  
  .method-description {
    display: block;
    font-size: 0.8rem;
    color: #777;
  }
}

// Options Grid
.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
}

.option-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    border-color: #b0b0b0;
    background: #fafafa;
  }
  
  &.is-selected {
    border-color: #28a745;
    background: #f0fff4;
    
    .option-checkbox i {
      color: #28a745;
    }
  }
  
  .option-checkbox {
    flex-shrink: 0;
    padding-top: 2px;
    
    i {
      font-size: 1.1rem;
      color: #aaa;
    }
  }
  
  .option-content {
    flex: 1;
    min-width: 0;
  }
  
  .option-label {
    display: block;
    font-weight: 500;
    font-size: 0.9rem;
    color: #333;
    margin-bottom: 0.15rem;
  }
  
  .option-description {
    display: block;
    font-size: 0.8rem;
    color: #777;
    line-height: 1.3;
  }
}

// Actions
.actions-section {
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
  margin-top: 0.5rem;
}

// Responsive
@media (max-width: 768px) {
  .payment-card {
    .card-header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem;
    }
  }
  
  .options-grid {
    grid-template-columns: 1fr;
  }
  
  .provider-header-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>
